import React, { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import CookieBanner from './components/CookieBanner';
import Layout from './components/Layout';

// ─── Lazy-loaded pages (code splitting) ──────────────────────────────────────
const Login              = lazy(() => import('./pages/Login'));
const Signup             = lazy(() => import('./pages/Signup'));
const Onboarding         = lazy(() => import('./pages/Onboarding'));
const Dashboard          = lazy(() => import('./pages/Dashboard'));
const AgencyDashboard    = lazy(() => import('./pages/AgencyDashboard'));
const AgencyPublic       = lazy(() => import('./pages/AgencyPublic'));
const ProjectsList       = lazy(() => import('./pages/ProjectsList'));
const NewProject         = lazy(() => import('./pages/NewProject'));
const ProjectDetail      = lazy(() => import('./pages/ProjectDetail'));
const BadgeEmbed         = lazy(() => import('./pages/BadgeEmbed'));
const ProfileBadge       = lazy(() => import('./pages/ProfileBadge'));
const Analytics          = lazy(() => import('./pages/Analytics'));
const Profile            = lazy(() => import('./pages/Profile'));
const Billing            = lazy(() => import('./pages/Billing'));
const Settings           = lazy(() => import('./pages/Settings'));
const Notifications      = lazy(() => import('./pages/Notifications'));
const MyBadges           = lazy(() => import('./pages/MyBadges'));
const Pricing            = lazy(() => import('./pages/Pricing'));
const Search             = lazy(() => import('./pages/Search'));
const Pipeline           = lazy(() => import('./pages/Pipeline'));
const Services           = lazy(() => import('./pages/Services'));
const Support            = lazy(() => import('./pages/Support'));
const CaseStudyPublic    = lazy(() => import('./pages/CaseStudyPublic'));
const EmployerProfile    = lazy(() => import('./pages/EmployerProfile'));
const PublicProfile      = lazy(() => import('./pages/PublicProfile'));
const PublicProject      = lazy(() => import('./pages/PublicProject'));
const VerifyPage         = lazy(() => import('./pages/VerifyPage'));
const LookupPage         = lazy(() => import('./pages/LookupPage'));
const VerifyFreelancerResults    = lazy(() => import('./pages/VerifyFreelancerResults'));
const VerifiedProfessionalRecords = lazy(() => import('./pages/VerifiedProfessionalRecords'));
const Assess                     = lazy(() => import('./pages/Assess'));

const AdminLayout        = lazy(() => import('./admin/AdminLayout'));
const AdminOverview      = lazy(() => import('./admin/AdminOverview'));
const AdminUsers         = lazy(() => import('./admin/AdminUsers'));
const AdminUserDetail    = lazy(() => import('./admin/AdminUserDetail'));
const AdminProjects      = lazy(() => import('./admin/AdminProjects'));
const AdminDisputes      = lazy(() => import('./admin/AdminDisputes'));
const AdminConsents      = lazy(() => import('./admin/AdminConsents'));
const AdminSupport       = lazy(() => import('./admin/AdminSupport'));
const AdminBroadcast     = lazy(() => import('./admin/AdminBroadcast'));
const AdminFraudFlags    = lazy(() => import('./admin/AdminFraudFlags'));
const AdminAssessLeads   = lazy(() => import('./admin/AdminAssessLeads'));

import api from './api';

// Shared suspense fallback
function PageLoader() {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.875rem' }}>Loading...</div>
    </div>
  );
}

// Google OAuth callback handler
function AuthCallback() {
  const navigate = useNavigate();
  const { login } = useAuth();
  useEffect(() => {
    const hash = window.location.hash;
    const match = hash.match(/google_token=([^&]+)/);
    if (!match) { navigate('/login'); return; }
    const token = decodeURIComponent(match[1]);
    // Fetch user data using the JWT returned from the backend OAuth callback
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(user => {
        login(token, user);
        if (user.suspended) navigate('/suspended');
        else if (!user.onboarding_complete) navigate('/onboarding');
        else navigate('/dashboard');
      })
      .catch(() => navigate('/login'));
  }, []);
  return (
    <div style={{ minHeight: '100vh', background: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: 'rgba(255,255,255,0.5)' }}>Signing you in...</div>
    </div>
  );
}

// Suspension page
function SuspendedPage() {
  const { logout } = useAuth();
  return (
    <div style={{ minHeight: '100vh', background: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
      <div style={{ textAlign: 'center', maxWidth: 420 }}>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 120" height="48" style={{ display: 'block', margin: '0 auto 2rem' }}>
          <defs><mask id="sm"><rect x="30" y="35" width="50" height="50" rx="10" fill="white"/><path d="M40 60 L50 70 L78 35" fill="none" stroke="black" strokeWidth="8" strokeLinecap="square"/></mask></defs>
          <rect x="30" y="35" width="50" height="50" rx="10" fill="#2260C4" mask="url(#sm)"/>
          <text x="96" y="68" fontFamily="Arial,sans-serif" fill="#FFFFFF" fontSize="32" letterSpacing="-1"><tspan fontWeight="700">VETT</tspan><tspan fontWeight="400" dx="5">WORK</tspan></text>
        </svg>
        <h1 style={{ color: '#fff', fontSize: '1.4rem', fontWeight: 700, marginBottom: '.75rem' }}>Account suspended</h1>
        <p style={{ color: 'rgba(255,255,255,.5)', fontSize: '.9rem', lineHeight: 1.6, marginBottom: '1.75rem' }}>
          Your account has been suspended. Please contact{' '}
          <a href="mailto:support@getvett.org" style={{ color: 'var(--blue3)' }}>support@getvett.org</a> for more information.
        </p>
        <button onClick={() => { logout(); window.location.replace('/landing.html'); }}
          style={{ background: 'rgba(255,255,255,.1)', color: '#fff', border: '1px solid rgba(255,255,255,.15)', padding: '.65rem 1.5rem', borderRadius: 8, cursor: 'pointer', fontFamily: 'var(--sys)', fontSize: '.875rem' }}>
          Sign out
        </button>
      </div>
    </div>
  );
}

// Impersonation banner
function ImpersonationBanner() {
  const { user, stopImpersonation, isImpersonating } = useAuth();
  if (!isImpersonating) return null;
  return (
    <div data-testid="impersonation-banner" style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999, background: '#B7770D', color: '#fff', padding: '.5rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', fontSize: '.83rem', fontWeight: 500 }}>
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="6" stroke="#fff" strokeWidth="1.4"/><text x="7" y="10.5" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="700">i</text></svg>
      Viewing as <strong>{user?.email}</strong>
      <button onClick={() => { stopImpersonation(); window.location.replace('/admin/users'); }}
        style={{ background: 'rgba(0,0,0,.25)', color: '#fff', border: '1px solid rgba(255,255,255,.3)', padding: '.2rem .75rem', borderRadius: 6, cursor: 'pointer', fontFamily: 'var(--sys)', fontSize: '.75rem' }}>
        Exit
      </button>
    </div>
  );
}

// Protected route — requires auth
function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ minHeight: '100vh', background: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div style={{ color: 'rgba(255,255,255,0.4)' }}>Loading...</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Protected route — requires onboarding + not suspended
function RequireDashboard({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ minHeight: '100vh', background: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div style={{ color: 'rgba(255,255,255,0.4)' }}>Loading...</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.suspended && !user._impersonating) return <Navigate to="/suspended" replace />;
  if (!user.onboarding_complete) return <Navigate to="/onboarding" replace />;
  return children;
}

// Admin only route
function RequireAdmin({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ minHeight: '100vh', background: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div style={{ color: 'rgba(255,255,255,0.4)' }}>Loading...</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin && user.role !== 'manager') return <Navigate to="/dashboard" replace />;
  return children;
}

function AdminIndex() {
  const { user } = useAuth();
  if (!user?.is_admin && user?.role === 'manager') return <Navigate to="/admin/users" replace />;
  return <AdminOverview />;
}

// Mobile viewport detection hook (kept for potential future use)
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    const handler = e => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

// Landing page — authenticated users go to dashboard, everyone else sees landing.html
function LandingPage() {
  const { user, loading } = useAuth();

  useEffect(() => {
    if (loading) return;
    if (user) {
      window.location.replace('/dashboard');
    } else {
      window.location.replace('/landing.html');
    }
  }, [user, loading]);

  return null;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes('google_token=')) return <AuthCallback />;
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/suspended" element={<SuspendedPage />} />
      <Route path="/onboarding" element={<RequireAuth><Onboarding /></RequireAuth>} />
      <Route path="/verify/:token" element={<VerifyPage />} />
      <Route path="/u/:slug" element={<PublicProfile />} />
      <Route path="/p/:id" element={<PublicProject />} />
      <Route path="/search" element={<Search />} />
      <Route path="/agency/:slug" element={<AgencyPublic />} />
      <Route path="/case-study/:id" element={<CaseStudyPublic />} />
      <Route path="/company/:slug" element={<EmployerProfile />} />
      <Route path="/lookup/:vw_id" element={<LookupPage />} />
      <Route path="/verify-freelancer-results" element={<VerifyFreelancerResults />} />
      <Route path="/verified-professional-records" element={<VerifiedProfessionalRecords />} />
      <Route path="/assess" element={<Assess />} />
      <Route path="/accessibility" element={<AccessibilityPage />} />
      <Route path="/terms" element={<StaticRedirect page="/terms.html" />} />
      <Route path="/privacy" element={<StaticRedirect page="/privacy.html" />} />
      <Route path="/contact" element={<StaticRedirect page="/contact.html" />} />
      <Route path="/demo" element={<StaticRedirect page="/demo.html" />} />

      {/* Dashboard */}
      <Route path="/dashboard" element={<RequireDashboard><Layout /></RequireDashboard>}>
        <Route index element={<Dashboard />} />
        <Route path="projects" element={<ProjectsList />} />
        <Route path="projects/new" element={<NewProject />} />
        <Route path="projects/:id" element={<ProjectDetail />} />
        <Route path="badge" element={<ProfileBadge />} />
        <Route path="badge/:id" element={<BadgeEmbed />} />
        <Route path="badges" element={<MyBadges />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="profile" element={<Profile />} />
        <Route path="billing" element={<Billing />} />
        <Route path="pricing" element={<Pricing />} />
        <Route path="settings" element={<Settings />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="pipeline" element={<Pipeline />} />
        <Route path="agency" element={<AgencyDashboard />} />
        <Route path="services" element={<Services />} />
        <Route path="support" element={<Support />} />
      </Route>

      {/* Admin panel */}
      <Route path="/admin" element={<RequireAdmin><AdminLayout /></RequireAdmin>}>
        <Route index element={<AdminIndex />} />
        <Route path="overview" element={<AdminOverview />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="users/:id" element={<AdminUserDetail />} />
        <Route path="projects" element={<AdminProjects />} />
        <Route path="disputes" element={<AdminDisputes />} />
        <Route path="consents" element={<AdminConsents />} />
        <Route path="support" element={<AdminSupport />} />
        <Route path="broadcast" element={<AdminBroadcast />} />
        <Route path="fraud-flags" element={<AdminFraudFlags />} />
        <Route path="assess-leads" element={<AdminAssessLeads />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function StaticRedirect({ page }) {
  useEffect(() => { window.location.replace(page); }, [page]);
  return null;
}

function ForgotPassword() {
  const [email, setEmail] = React.useState('');
  const [sent, setSent] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try { await api.post('/auth/forgot-password', { email }); setSent(true); }
    catch (err) { setError(err.response?.data?.detail || 'Something went wrong. Please try again.'); }
    finally { setLoading(false); }
  };
  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1 style={{ color: '#fff', fontSize: '1.2rem', fontWeight: 700, marginBottom: '1.25rem' }}>Reset password</h1>
        {sent ? <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.875rem' }}>Reset link sent. Check your email.</p> : (
          <form onSubmit={handleSubmit}>
            {error && <p style={{ color: '#f87171', fontSize: '.8rem', marginBottom: '.75rem' }}>{error}</p>}
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="vw-input" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading} style={{ width: '100%', justifyContent: 'center' }}>
              {loading ? 'Sending...' : 'Send reset link'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function ResetPassword() {
  const [pwd, setPwd] = React.useState('');
  const [done, setDone] = React.useState(false);
  const [error, setError] = React.useState('');
  const token = new URLSearchParams(window.location.search).get('token');
  const handleSubmit = async (e) => {
    e.preventDefault(); setError('');
    try { await api.post('/auth/reset-password', { token, password: pwd }); setDone(true); }
    catch (err) { setError(err.response?.data?.detail || 'Invalid or expired link.'); }
  };
  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1 style={{ color: '#fff', fontSize: '1.2rem', fontWeight: 700, marginBottom: '1.25rem' }}>Set new password</h1>
        {done ? <p style={{ color: 'rgba(255,255,255,0.6)' }}>Password updated. <a href="/login" style={{ color: 'var(--blue3)' }}>Sign in →</a></p> : (
          <form onSubmit={handleSubmit}>
            {error && <p style={{ color: '#f87171', fontSize: '.8rem', marginBottom: '.75rem' }}>{error}</p>}
            <div className="form-group">
              <label className="form-label">New password</label>
              <input className="vw-input" type="password" value={pwd} onChange={e => setPwd(e.target.value)} minLength={8} required />
            </div>
            <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}>Update password</button>
          </form>
        )}
      </div>
    </div>
  );
}

function AccessibilityPage() {
  return (
    <div className="auth-page" style={{ alignItems: 'flex-start', paddingTop: '3rem' }}>
      <div style={{ maxWidth: 700, margin: '0 auto', padding: '0 2rem', width: '100%' }}>
        <a href="/" style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', textDecoration: 'none' }}>← Back to home</a>
        <h1 style={{ color: '#fff', fontSize: '2rem', fontWeight: 700, marginTop: '1.5rem', marginBottom: '1rem' }}>Accessibility</h1>
        <p style={{ color: 'rgba(255,255,255,0.65)', lineHeight: 1.8, marginBottom: '1.25rem', fontSize: '0.95rem' }}>
          VETT Work is committed to making our platform accessible to everyone. We aim to conform to WCAG 2.1 Level AA.
        </p>
        <p style={{ color: 'rgba(255,255,255,0.65)', lineHeight: 1.8, marginBottom: '1.25rem', fontSize: '0.95rem' }}>
          If you experience any accessibility barriers, please contact us at{' '}
          <a href="mailto:contact@getvett.org" style={{ color: 'var(--blue3)', textDecoration: 'none' }}>contact@getvett.org</a>{' '}
          and we will work to resolve the issue.
        </p>
        <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.83rem', marginTop: '2rem' }}>Last reviewed: March 2026</p>
      </div>
    </div>
  );
}

function VerifyEmailPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = React.useState('loading'); // loading | success | error
  const [errorMsg, setErrorMsg] = React.useState('');
  const token = new URLSearchParams(window.location.search).get('token');

  React.useEffect(() => {
    if (!token) { setStatus('error'); setErrorMsg('No verification token found.'); return; }
    api.get(`/auth/verify-email?token=${token}`)
      .then(res => {
        login(res.data.token, res.data.user);
        setStatus('success');
        setTimeout(() => {
          navigate(res.data.user?.onboarding_complete ? '/dashboard' : '/onboarding', { replace: true });
        }, 2500);
      })
      .catch(err => {
        setStatus('error');
        setErrorMsg(err.response?.data?.detail || 'Invalid or expired verification link.');
      });
  }, []);

  return (
    <div className="auth-page">
      <div className="auth-card" style={{ textAlign: 'center' }}>
        {status === 'loading' && (
          <>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.875rem', marginBottom: '0.5rem' }}>Verifying your email...</div>
          </>
        )}
        {status === 'success' && (
          <>
            <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(26,188,156,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M4 11l5 5L18 6" stroke="#1ABC9C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
            <h2 style={{ color: '#fff', fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.5rem' }}>Email verified!</h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.875rem' }}>Taking you to your dashboard...</p>
          </>
        )}
        {status === 'error' && (
          <>
            <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(146,43,33,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M11 7v6M11 15.5v.5" stroke="#922B21" strokeWidth="2.5" strokeLinecap="round"/></svg>
            </div>
            <h2 style={{ color: '#fff', fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.5rem' }}>Verification failed</h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.875rem', marginBottom: '1rem' }}>{errorMsg}</p>
            <a href="/login" style={{ color: 'var(--blue3)', fontSize: '0.875rem', textDecoration: 'none' }}>Sign in to request a new link →</a>
          </>
        )}
      </div>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <a href="#main-content" className="skip-link">Skip to main content</a>
        <ImpersonationBanner />
        <AppRoutes />
        <CookieBanner />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
